import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {ArrowRightIcon} from '@amblique/king-living/app/components/icons';
import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

import {useMenuItemById} from '@amblique/contentful/app/contentful/hooks/use-menu-item';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {Image as ContentfulImage} from '@amblique/king-living/app/contentful/models/image';
import {NavigationColumn as ContentfulNavigationColumn} from '@amblique/king-living/app/contentful/models/navigation-column';

const isIncompleteMenuItem = (menuItem) => {
  if (!menuItem?.sys?.id || !menuItem.title) {
    return false;
  }

  const hasSubmenuField = 'submenuCollection' in menuItem;
  const hasLinkField = 'link' in menuItem;

  return !hasLinkField && !hasSubmenuField;
};

const hasSubmenu = (menuItem) => {
  return menuItem?.submenuCollection?.items?.length > 0;
};

// Render for desktop
const renderForDesktop = (hasImages, images, menuItem, submenuItems, styles, props) => {
  const safeSubmenuItems = ensureArray(submenuItems);

  return (
    <Box
      maxW="container.xxxl"
      mx="auto"
      p={6}
      width="100%"
      {...props}
    >
      <Flex alignItems="flex-start" flexDirection="row" gap={6}>
        {safeSubmenuItems.map((subItem) => {
          if (subItem?.__typename === 'NavigationColumn' || subItem?.linksCollection) {
            return (
              <ContentfulNavigationColumn
                key={subItem.sys?.id}
                component={subItem}
              />
            );
          }

          return null;
        })}

        {hasImages && (
          <Flex alignItems="center" flexDirection="row" flexWrap="nowrap" gap={2}>
            {images.map((image) => (
              <ContentfulImage key={image.sys?.id} component={image} />
            ))}
          </Flex>
        )}
      </Flex>
      {menuItem?.featuredLink && (
        <Flex alignItems="center" flexDirection="row" flexWrap="wrap" gap={2}>
          <ContentfulLink className="shop-all-button" component={menuItem.featuredLink}>
            <FormattedMessage defaultMessage="Shop all" id="mega_menu.shop_all_link" /> <ArrowRightIcon ml={2} />
          </ContentfulLink>
        </Flex>
      )}
    </Box>
  );
};

// Render for mobile
const renderForMobile = (hasImages, hasSubmenuItems, handleClick, link, submenuItems, images, styles, props) => {
  const safeSubmenuItems = ensureArray(submenuItems);

  return (
    <Box className="menu-item-renderer" {...props}>
      <Box className="submenu-items-mobile">
        {safeSubmenuItems.length > 0 && safeSubmenuItems.map((subItem) => (
          <Box key={subItem.sys?.id} className="submenu-item-mobile" {...styles.menuItemContainer}>
            <ContentfulNavigationColumn
              component={subItem}
            />
          </Box>
        ))}

        {hasImages && (
          <Flex {...styles.menuItemImages} mt={2}>
            {images.map((image) => (
              <ContentfulImage key={image.sys?.id} component={image} />
            ))}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export const MenuItemContent = ({
  menuItem,
  onItemClick,
  isMobile = false,
  submenuItems = [],
  ...props
}) => {
  const menuItemId = menuItem?.sys?.id;
  const needsFullData = isIncompleteMenuItem(menuItem);

  const {data: fullMenuItemData} = useMenuItemById(menuItemId, {
    variant: 'default',
    enabled: needsFullData && !!menuItemId,
  });

  // Use full data if available, otherwise use passed menuItem
  const resolvedMenuItem = fullMenuItemData || menuItem;
  const hasSubmenuItems = hasSubmenu(resolvedMenuItem);

  const handleClick = (e) => {
    if (hasSubmenuItems && onItemClick) {
      e.preventDefault();
      onItemClick(resolvedMenuItem);
    }
  };

  const styles = useMultiStyleConfig('MegaMenu', {isMobile});

  const link = resolvedMenuItem?.link || null;
  const imagesCollection = resolvedMenuItem?.imagesCollection;
  const images = ensureArray(imagesCollection?.items);
  const hasImages = images.length > 0;

  return (
    <Box className="menu-item-content" {...props}>
      {isMobile ? renderForMobile(hasImages, hasSubmenuItems, handleClick, link, submenuItems, images, styles, props) :
        renderForDesktop(hasImages, images, resolvedMenuItem, submenuItems, styles, props)}
    </Box>
  );
};

MenuItemContent.displayName = 'MenuItemContent';
MenuItemContent.propTypes = {
  isMobile: PropTypes.bool,
  menuItem: PropTypes.object.isRequired,
  onItemClick: PropTypes.func,
  submenuItems: PropTypes.array,
};
